/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import type { Position } from '../../nodes/InlineImageNode/InlineImageNode';
import {
  $createInlineImageNode,
  $isInlineImageNode,
  InlineImageNode,
  InlineImagePayload,
} from '../../nodes/InlineImageNode/InlineImageNode';

import '../../nodes/InlineImageNode/InlineImageNode.css';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $createRangeSelection,
  $getNodeByKey,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  LexicalCommand,
  LexicalEditor,
  NodeKey,
} from 'lexical';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { CAN_USE_DOM } from 'shared/canUseDOM';
import clsx from 'clsx';
import styles from '../commonStyles.module.scss';
import {
  Button,
  ButtonColor,
} from '../../../../../../components/uikit/buttons/Button.tsx';
import { Input } from '../../../../../../components/uikit/inputs/Input.tsx';
import { useScopedTranslation } from '../../../../../../application/localization/useScopedTranslation.ts';
import CloseIcon from 'assets/icons/close-button-small.svg?react';
import { Checkbox } from '../../../../../../components/uikit/inputs/Checkbox.tsx';
import { RadioButton } from '../../../../../../components/uikit/RadioButton.tsx';

export type InsertInlineImagePayload = Readonly<InlineImagePayload>;

const getDOMSelection = (targetWindow: Window | null): Selection | null =>
  CAN_USE_DOM ? (targetWindow || window).getSelection() : null;

export const INSERT_INLINE_IMAGE_COMMAND: LexicalCommand<InlineImagePayload> =
  createCommand('INSERT_INLINE_IMAGE_COMMAND');

const positions = [
  {
    title: 'Left',
    value: 'left',
  },
  {
    title: 'Right',
    value: 'right',
  },
  {
    title: 'Full width',
    value: 'full',
  },
];

export function InsertInlineImageDialog({
  activeEditor,
  onClose,
  nodeKey,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
  nodeKey?: NodeKey;
}): React.JSX.Element {
  const hasModifier = useRef(false);

  const editorState = activeEditor.getEditorState();
  const node = editorState.read(
    () => $getNodeByKey(nodeKey ?? '') as InlineImageNode,
  );

  const [src, setSrc] = useState('');
  const [altText, setAltText] = useState(node?.getAltText() || '');
  const [showCaption, setShowCaption] = useState(
    node?.getShowCaption() || false,
  );
  const [position, setPosition] = useState<Position>(
    node?.getPosition() || 'left',
  );
  const [imageName, setImageName] = useState('');

  const i18n = useScopedTranslation('Page.PageDesigner');

  const uploadFileRef = useRef<HTMLInputElement>(null);
  const triggerFileUpload = () => {
    uploadFileRef.current!.click();
  };

  const isDisabled = src === '' && !node;

  const loadImage = (files: FileList | null) => {
    const reader = new FileReader();
    reader.onload = function () {
      if (typeof reader.result === 'string') {
        setSrc(reader.result);
        if (files) {
          setImageName(files[0]?.name);
        }
      }
      return '';
    };
    if (files?.length) {
      reader.readAsDataURL(files[0]);
    }
  };

  useEffect(() => {
    hasModifier.current = false;
    const handler = (e: KeyboardEvent) => {
      hasModifier.current = e.altKey;
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [activeEditor]);

  const handleOnClick = () => {
    const payload = { altText, position, showCaption, src };
    if (node) {
      activeEditor.update(() => {
        node.update(payload);
      });
    } else {
      activeEditor.dispatchCommand(INSERT_INLINE_IMAGE_COMMAND, payload);
    }
    onClose();
  };

  const deleteImage = () => {
    setSrc('');
    setImageName('');
  };

  return (
    <>
      {!node && (
        <>
          <input
            type="file"
            id="uploadFile"
            accept="image/*"
            ref={uploadFileRef}
            style={{ display: 'none' }}
            onChange={(e) => loadImage(e.target.files)}
          />
          <div className={clsx(styles.greyText, styles.marginBottom5)}>
            {i18n.t('upload_image')}
          </div>
          <div
            className={clsx(
              styles.rowContainer,
              styles.spaceBetween,
              styles.alignCenter,
              styles.marginBottom50,
            )}
          >
            {imageName ? (
              <div className={styles.rowContainer}>
                <div className={styles.regularText}>{imageName}</div>
                <CloseIcon className={styles.closeIcon} onClick={deleteImage} />
              </div>
            ) : (
              <div className={styles.greyText}>
                {i18n.t('no_image_uploaded')}
              </div>
            )}
            <Button
              title={i18n.t('select_file')}
              color={ButtonColor.Secondary}
              onClick={triggerFileUpload}
            />
          </div>
        </>
      )}
      <Input
        onChange={(e) => setAltText(e.target.value)}
        value={altText}
        topHelperText={i18n.t('alt_text')}
        fullWidth={true}
        containerClassName={clsx(node && styles.marginTop20)}
      />
      <div
        className={clsx(
          styles.greyText,
          styles.marginBottom10,
          styles.marginTop35,
        )}
      >
        {i18n.t('position')}
      </div>
      <div
        className={clsx(
          styles.rowContainer,
          styles.marginBottom35,
          styles.columnGap80,
        )}
      >
        {positions.map((elem) => (
          <RadioButton
            name={'position'}
            key={elem.value}
            title={elem.title}
            value={elem.value}
            onChange={(e) => setPosition(e.target.value as Position)}
            checked={position === elem.value}
          />
        ))}
      </div>
      <div className={clsx(styles.rowContainer, styles.columnGap7)}>
        <Checkbox
          isChecked={showCaption}
          onChange={() => setShowCaption((prev) => !prev)}
          actionText={i18n.t('show_caption')}
        />
      </div>
      <div className={styles.buttonContainer}>
        <Button
          onClick={onClose}
          title={i18n.t('cancel')}
          color={ButtonColor.Secondary}
        />
        <Button
          title={!node ? i18n.t('insert_image') : i18n.t('update_image')}
          color={ButtonColor.Primary}
          disabled={isDisabled}
          onClick={() => handleOnClick()}
        />
      </div>
    </>
  );
}

export default function InlineImagePlugin(): React.JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([InlineImageNode])) {
      throw new Error('ImagesPlugin: ImageNode not registered on editor');
    }

    return mergeRegister(
      editor.registerCommand<InsertInlineImagePayload>(
        INSERT_INLINE_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createInlineImageNode(payload);
          $insertNodes([imageNode]);
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand<DragEvent>(
        DRAGSTART_COMMAND,
        (event) => {
          return $onDragStart(event);
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand<DragEvent>(
        DRAGOVER_COMMAND,
        (event) => {
          return $onDragover(event);
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand<DragEvent>(
        DROP_COMMAND,
        (event) => {
          return $onDrop(event, editor);
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [editor]);

  return null;
}

const TRANSPARENT_IMAGE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const img = document.createElement('img');
img.src = TRANSPARENT_IMAGE;

function $onDragStart(event: DragEvent): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) {
    return false;
  }
  dataTransfer.setData('text/plain', '_');
  dataTransfer.setDragImage(img, 0, 0);
  dataTransfer.setData(
    'application/x-lexical-drag',
    JSON.stringify({
      data: {
        altText: node.__altText,
        caption: node.__caption,
        height: node.__height,
        key: node.getKey(),
        showCaption: node.__showCaption,
        src: node.__src,
        width: node.__width,
      },
      type: 'image',
    }),
  );

  return true;
}

function $onDragover(event: DragEvent): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  if (!canDropImage(event)) {
    event.preventDefault();
  }
  return true;
}

function $onDrop(event: DragEvent, editor: LexicalEditor): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const data = getDragImageData(event);
  if (!data) {
    return false;
  }
  event.preventDefault();
  if (canDropImage(event)) {
    const range = getDragSelection(event);
    node.remove();
    const rangeSelection = $createRangeSelection();
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range);
    }
    $setSelection(rangeSelection);
    editor.dispatchCommand(INSERT_INLINE_IMAGE_COMMAND, data);
  }
  return true;
}

function $getImageNodeInSelection(): InlineImageNode | null {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) {
    return null;
  }
  const nodes = selection.getNodes();
  const node = nodes[0];
  return $isInlineImageNode(node) ? node : null;
}

function getDragImageData(event: DragEvent): null | InsertInlineImagePayload {
  const dragData = event.dataTransfer?.getData('application/x-lexical-drag');
  if (!dragData) {
    return null;
  }
  const { type, data } = JSON.parse(dragData);
  if (type !== 'image') {
    return null;
  }

  return data;
}

declare global {
  interface DragEvent {
    rangeOffset?: number;
    rangeParent?: Node;
  }
}

function canDropImage(event: DragEvent): boolean {
  const target = event.target;
  return !!(
    target &&
    target instanceof HTMLElement &&
    !target.closest('code, span.editor-image') &&
    target.parentElement &&
    target.parentElement.closest('div.ContentEditable__root')
  );
}

function getDragSelection(event: DragEvent): Range | null | undefined {
  let range;
  const target = event.target as null | Element | Document;
  const targetWindow =
    target == null
      ? null
      : target.nodeType === 9
        ? (target as Document).defaultView
        : (target as Element).ownerDocument.defaultView;
  const domSelection = getDOMSelection(targetWindow);
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY);
  } else if (event.rangeParent && domSelection !== null) {
    domSelection.collapse(event.rangeParent, event.rangeOffset || 0);
    range = domSelection.getRangeAt(0);
  } else {
    throw Error('Cannot get the selection when dragging');
  }

  return range;
}
